import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { brandColors } from '@config/brandTokens';
import { useAuthStore } from '@store/authStore';
import {
  cancelSubscription,
  confirmStripeCheckout,
  fetchMySubscription,
  fetchPlayerFamilyCatalog,
} from '@services/subscriptionService';
import { writeSubscriptionCheckoutDraft } from '@/utils/subscriptionCheckout';
import ChangePlanDialog from '@components/billing/ChangePlanDialog';
import PaymentGatewayChooser from '@components/billing/PaymentGatewayChooser';
import PrebookDialog from '@components/billing/PrebookDialog';
import {
  DEFAULT_SUBSCRIPTION_OFFERS,
  fetchSubscriptionOffers,
  formatCatalogYearly,
  isPurchasableOffer,
  purchasePathForOffer,
  type SubscriptionOffer,
} from '@/content/subscriptionPricing';
import {
  audienceLabelForRole,
  clampFamilyPlanConfig,
  COACH_IN_APP_OFFER,
  defaultFamilyPlanConfig,
  familyIncludedSeats,
  earlyBirdUsuallyLine,
  familyPlanSummary,
  DISABLED_PLAN_LAUNCH_NOTE,
  findPlayerPlan,
  isOccupiedSubscription,
  isOrgAdminRole,
  isPlanEnabled,
  inAppDashboardPath,
  offersForAuthenticatedUser,
  preferredPaymentGateway,
  purchaseGatewayPath,
  showsPlayerFamilyPlans,
  planDisplayName,
  planFeaturesForCategory,
  planFromPrice,
  PLAYER_FAMILY_CATALOG,
  quoteFamilyPlan,
  subscriptionStatusLabel,
  type FamilyPlanConfig,
  type PlayerFamilyCatalog,
  type PlayerPlanOffering,
} from '@/content/subscriptionCatalog';
import type { PaymentGateway, PlayerPlanId, SubscriptionCategory } from '@types/index';
import type { UserSubscription } from '@types/index';

const quietCardSx = {
  p: { xs: 2, md: 2.5 },
  borderRadius: 2,
  bgcolor: brandColors.white,
  border: '1px solid #e2e8f0',
} as const;

function formatExpiry(value?: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function StepperRow({
  label,
  hint,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  hint: string;
  value: number;
  min: number;
  max: number;
  onChange: (next: number) => void;
}) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.25}
      alignItems={{ sm: 'center' }}
      justifyContent="space-between"
      sx={{ py: 1.25 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 800, color: brandColors.navy, fontSize: '0.95rem' }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color: brandColors.slate, lineHeight: 1.45 }}>
          {hint}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
        <Button
          size="small"
          variant="outlined"
          disabled={value <= min}
          onClick={() => onChange(value - 1)}
          sx={{ minWidth: 36, px: 0, borderColor: '#cbd5e1', color: brandColors.navy }}
          aria-label={`Decrease ${label}`}
        >
          <RemoveIcon fontSize="small" />
        </Button>
        <Typography sx={{ fontWeight: 800, minWidth: 28, textAlign: 'center', color: brandColors.navy }}>
          {value}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          disabled={value >= max}
          onClick={() => onChange(value + 1)}
          sx={{ minWidth: 36, px: 0, borderColor: '#cbd5e1', color: brandColors.navy }}
          aria-label={`Increase ${label}`}
        >
          <AddIcon fontSize="small" />
        </Button>
      </Stack>
    </Stack>
  );
}

function CategoryToggle({
  value,
  disabled,
  onChange,
}: {
  value: SubscriptionCategory;
  disabled?: boolean;
  onChange: (next: SubscriptionCategory) => void;
}) {
  const options: Array<{ id: SubscriptionCategory; label: string }> = [
    { id: 'individual', label: 'Individual' },
    { id: 'family', label: 'Family' },
  ];
  return (
    <Box
      role="tablist"
      aria-label="Plan category"
      sx={{
        display: 'inline-flex',
        p: 0.35,
        borderRadius: 999,
        bgcolor: brandColors.ice,
        border: '1px solid #e2e8f0',
        gap: 0.35,
      }}
    >
      {options.map((option) => {
        const selected = value === option.id;
        return (
          <Box
            key={option.id}
            role="tab"
            aria-selected={selected}
            component="button"
            type="button"
            disabled={disabled}
            onClick={() => onChange(option.id)}
            sx={{
              border: 0,
              cursor: disabled ? 'default' : 'pointer',
              px: { xs: 1.75, sm: 2.25 },
              py: 0.7,
              borderRadius: 999,
              bgcolor: selected ? brandColors.navy : 'transparent',
              color: selected ? brandColors.white : brandColors.slate,
              fontFamily: '"Sora", Arial, sans-serif',
              fontWeight: 800,
              fontSize: '0.82rem',
              lineHeight: 1.2,
              transition: 'background-color 0.15s ease, color 0.15s ease',
              '&:hover': disabled
                ? undefined
                : { color: selected ? brandColors.white : brandColors.navy },
            }}
          >
            {option.label}
          </Box>
        );
      })}
    </Box>
  );
}

function PlanCard({
  plan,
  features,
  selected,
  disabled,
  greyed,
  fromPrice,
  onSelect,
}: {
  plan: PlayerPlanOffering;
  features: string[];
  selected: boolean;
  disabled?: boolean;
  greyed?: boolean;
  fromPrice: string;
  onSelect: () => void;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      disabled={disabled}
      sx={{
        ...quietCardSx,
        width: '100%',
        textAlign: 'left',
        cursor: disabled ? 'default' : 'pointer',
        border: selected ? `2px solid ${brandColors.navy}` : '1px solid #e2e8f0',
        bgcolor: greyed ? '#f1f5f9' : selected ? '#f8fafc' : brandColors.white,
        opacity: greyed ? 0.72 : 1,
        filter: greyed ? 'grayscale(0.35)' : 'none',
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
        '&:hover': disabled
          ? undefined
          : { borderColor: selected ? brandColors.navy : '#94a3b8' },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} useFlexGap flexWrap="wrap">
        <Typography
          sx={{
            fontWeight: 800,
            color: greyed ? brandColors.slate : brandColors.navy,
            fontFamily: '"Sora", Arial, sans-serif',
            fontSize: '1.15rem',
          }}
        >
          {plan.name}
        </Typography>
        {plan.highlight && !greyed && (
          <Chip
            size="small"
            label="Most features"
            sx={{ height: 22, fontWeight: 800, bgcolor: brandColors.ice, color: brandColors.navy }}
          />
        )}
        {greyed && (
          <Chip
            size="small"
            label="Launching later"
            sx={{ height: 22, fontWeight: 800, bgcolor: '#e2e8f0', color: brandColors.slate }}
          />
        )}
      </Stack>
      <Typography variant="body2" sx={{ color: brandColors.slate, mb: 1.25 }}>
        {plan.tagline}
      </Typography>
      {greyed ? (
        <Typography variant="body2" sx={{ color: brandColors.slate, fontWeight: 700, mb: 1.5, lineHeight: 1.45 }}>
          {DISABLED_PLAN_LAUNCH_NOTE}
        </Typography>
      ) : (
        <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ mb: 1.5 }}>
          <Typography
            sx={{
              fontFamily: '"Sora", Arial, sans-serif',
              fontWeight: 900,
              fontSize: '1.55rem',
              color: brandColors.navy,
              lineHeight: 1,
            }}
          >
            {fromPrice}
          </Typography>
          <Typography sx={{ color: brandColors.slate, fontWeight: 600, fontSize: '0.85rem' }}>/ year</Typography>
        </Stack>
      )}
      <Stack spacing={0.75}>
        {features.map((feature) => (
          <Stack key={feature} direction="row" spacing={1} alignItems="flex-start">
            <CheckIcon sx={{ fontSize: 17, color: greyed ? '#94a3b8' : brandColors.blue, mt: '2px' }} />
            <Typography sx={{ fontSize: '0.88rem', color: greyed ? brandColors.slate : '#1e293b', lineHeight: 1.4 }}>
              {feature}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

interface DashboardSubscriptionPanelProps {
  academyType?: string | null;
  familyHint?: Partial<FamilyPlanConfig> | null;
  country?: string | null;
}

const DashboardSubscriptionPanel: React.FC<DashboardSubscriptionPanelProps> = ({
  academyType,
  familyHint,
  country,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, setUser } = useAuthStore();
  const [offers, setOffers] = useState<SubscriptionOffer[]>(DEFAULT_SUBSCRIPTION_OFFERS);
  const [subscription, setSubscription] = useState<UserSubscription | null>(user?.subscription || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [changeOpen, setChangeOpen] = useState(false);
  const [prebookOpen, setPrebookOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [catalog, setCatalog] = useState<PlayerFamilyCatalog>(PLAYER_FAMILY_CATALOG);
  const [family, setFamily] = useState<FamilyPlanConfig>(() =>
    clampFamilyPlanConfig(user?.subscription?.planConfig || familyHint || defaultFamilyPlanConfig()),
  );
  const [payGateway, setPayGateway] = useState<PaymentGateway>(() =>
    preferredPaymentGateway(PLAYER_FAMILY_CATALOG.currency, PLAYER_FAMILY_CATALOG.countryCode),
  );
  const [gatewayTouched, setGatewayTouched] = useState(false);

  const role = String(user?.role || '');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [mine, offerList, familyCatalog] = await Promise.all([
        fetchMySubscription(),
        fetchSubscriptionOffers(),
        fetchPlayerFamilyCatalog(country),
      ]);
      if (cancelled) return;
      if (offerList.length) setOffers(offerList);
      if (familyCatalog) setCatalog(familyCatalog);
      if (mine) {
        setSubscription(mine);
        if (user) setUser({ ...user, subscription: mine });
        if (mine.planConfig) setFamily(clampFamilyPlanConfig(mine.planConfig, familyCatalog));
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, country]);

  useEffect(() => {
    if (subscription?.planConfig || !familyHint) return;
    setFamily((prev) =>
      clampFamilyPlanConfig(
        {
          ...prev,
          childrenCount: familyHint.childrenCount ?? prev.childrenCount,
          extraChildren: familyHint.extraChildren ?? prev.extraChildren,
          publicProfile: familyHint.publicProfile ?? prev.publicProfile,
        },
        catalog,
      ),
    );
  }, [familyHint?.childrenCount, familyHint?.extraChildren, familyHint?.publicProfile, subscription?.planConfig, catalog]);

  useEffect(() => {
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    if (!payment) return;
    let cancelled = false;
    (async () => {
      if (payment === 'cancelled') {
        if (!cancelled) {
          setError(
            'Payment was cancelled. Your plan is not active until you complete Stripe or Razorpay checkout.',
          );
        }
      } else if (payment === 'success' && sessionId) {
        try {
          const paid = await confirmStripeCheckout(sessionId);
          if (cancelled) return;
          setSubscription(paid);
          if (user) setUser({ ...user, subscription: paid });
          setError('');
        } catch (err: unknown) {
          const anyErr = err as { response?: { data?: { message?: string } }; message?: string };
          if (!cancelled) {
            setError(anyErr.response?.data?.message || anyErr.message || 'Could not confirm payment');
          }
        }
      }
      const next = new URLSearchParams(searchParams);
      next.delete('payment');
      next.delete('session_id');
      setSearchParams(next, { replace: true });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('payment'), searchParams.get('session_id')]);

  const relevant = useMemo(
    () => offersForAuthenticatedUser(offers, role, academyType),
    [offers, role, academyType],
  );
  const playerOffer = relevant.find((o) => o.id === 'players_parents') || relevant[0];
  const selectedPlan = findPlayerPlan(family.planId, catalog);
  const quote = showsPlayerFamilyPlans(role) ? quoteFamilyPlan(playerOffer, family, catalog) : null;
  const occupied = isOccupiedSubscription(subscription?.status);
  const active = subscription?.status === 'active';
  const expiry = formatExpiry(subscription?.expiresAt);
  const money = (amount: number) => formatCatalogYearly(amount, catalog.currency);
  const selectedEnabled = isPlanEnabled(selectedPlan, family.category);
  const canBuy = !occupied && selectedEnabled && user?.canEditProfile !== false;
  const suggestedGateway = preferredPaymentGateway(catalog.currency, catalog.countryCode);

  useEffect(() => {
    if (gatewayTouched) return;
    setPayGateway(preferredPaymentGateway(catalog.currency, catalog.countryCode));
  }, [catalog.currency, catalog.countryCode, gatewayTouched]);

  const updateFamily = (patch: Partial<FamilyPlanConfig>) => {
    setFamily((prev) => clampFamilyPlanConfig({ ...prev, ...patch }, catalog));
  };

  const handlePurchase = () => {
    if (!isPlanEnabled(selectedPlan, family.category)) {
      setError(DISABLED_PLAN_LAUNCH_NOTE);
      return;
    }
    if (!playerOffer || !isPurchasableOffer(playerOffer)) {
      const offer = relevant[0] || COACH_IN_APP_OFFER;
      navigate(purchasePathForOffer(offer));
      return;
    }
    if (quote?.payableUsd == null) {
      setError('Choose a yearly plan to continue.');
      return;
    }
    setError('');
    const planLabel = planDisplayName(family);
    writeSubscriptionCheckoutDraft({
      offerId: playerOffer.id,
      family,
      amount: quote.payableUsd,
      currency: catalog.currency,
      planLabel,
      summary: familyPlanSummary(family),
      listUsd: quote.listUsd,
      discountPercent: quote.discountPercent,
      earlyBirdActive: quote.earlyBirdActive,
      provider: payGateway,
    });
    navigate(
      purchaseGatewayPath({
        role,
        amount: quote.payableUsd,
        plan: planLabel,
        returnUrl: inAppDashboardPath(role),
        currency: catalog.currency,
        countryCode: catalog.countryCode,
        gateway: payGateway,
      }),
    );
  };

  const applySubscription = (next: UserSubscription) => {
    setSubscription(next);
    if (user) setUser({ ...user, subscription: next });
  };

  const handleCancel = async () => {
    setCancelling(true);
    setError('');
    try {
      applySubscription(await cancelSubscription());
      setCancelOpen(false);
    } catch (err: unknown) {
      const anyErr = err as { response?: { data?: { message?: string } }; message?: string };
      setError(anyErr.response?.data?.message || anyErr.message || 'Could not cancel');
    } finally {
      setCancelling(false);
    }
  };

  if (!user) return null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Box sx={quietCardSx}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography
            sx={{
              fontWeight: 800,
              color: brandColors.navy,
              fontFamily: '"Sora", Arial, sans-serif',
              fontSize: '1.05rem',
            }}
          >
            Subscription
          </Typography>
          <Chip
            size="small"
            label={subscriptionStatusLabel(subscription?.status)}
            sx={{
              height: 22,
              fontWeight: 800,
              bgcolor:
                subscription?.status === 'prebooked'
                  ? '#fff7ed'
                  : active || subscription?.status === 'beta'
                    ? '#ecfdf5'
                    : brandColors.ice,
              color:
                subscription?.status === 'prebooked'
                  ? '#c2410c'
                  : active || subscription?.status === 'beta'
                    ? '#047857'
                    : brandColors.navy,
            }}
          />
        </Stack>
        <Typography variant="body2" sx={{ color: brandColors.slate, lineHeight: 1.5 }}>
          {occupied
            ? `${subscription?.planConfig ? familyPlanSummary(subscription.planConfig) : audienceLabelForRole(role, academyType)} · yearly${
                subscription?.amountUsd != null
                  ? ` · ${formatCatalogYearly(subscription.amountUsd, subscription.currency || catalog.currency)} / year`
                  : ''
              }${expiry ? ` · renews ${expiry}` : ''}${
                subscription?.status === 'prebooked'
                  ? ` · starts ${formatExpiry(subscription.planConfig?.startDate || subscription.startedAt) || expiry || '—'}`
                  : ''
              }`
            : `Plans for ${audienceLabelForRole(role, academyType)} only. Choose a yearly plan here. Sports are chosen after payment.`}
        </Typography>
        {showsPlayerFamilyPlans(role) && occupied && (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
            <Button
              size="small"
              variant="contained"
              onClick={() => setChangeOpen(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                bgcolor: brandColors.navy,
                '&:hover': { bgcolor: '#0a0f28' },
              }}
            >
              Upgrade / Downgrade
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setCancelOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 800, borderColor: '#cbd5e1', color: brandColors.slate }}
            >
              Cancel subscription
            </Button>
          </Stack>
        )}
      </Box>

      {showsPlayerFamilyPlans(role) && playerOffer && (
        <>
          <Box>
            <Typography
              sx={{
                fontWeight: 800,
                color: brandColors.navy,
                fontFamily: '"Sora", Arial, sans-serif',
                fontSize: '1.05rem',
              }}
            >
              {catalog.headline}
            </Typography>
            <Typography variant="body2" sx={{ color: brandColors.slate, mt: 0.5, mb: 1.25 }}>
              {family.category === 'family'
                ? `Family includes ${familyIncludedSeats(catalog)}. Additional children are extra. Same yearly price for every sport — choose sports after payment.`
                : 'One player seat. Same yearly price for every sport — choose sports after payment.'}
            </Typography>
            <CategoryToggle
              value={family.category}
              disabled={occupied}
              onChange={(category) => {
                if (!occupied) updateFamily({ category });
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
              gap: 1.5,
            }}
          >
            {catalog.plans.map((plan) => {
              const from = planFromPrice(plan, catalog, playerOffer, family.category);
              const greyed = !isPlanEnabled(plan, family.category);
              return (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  features={planFeaturesForCategory(plan, family.category, catalog)}
                  selected={family.planId === plan.id}
                  disabled={occupied}
                  greyed={greyed}
                  fromPrice={money(from.payableUsd)}
                  onSelect={() => {
                    if (occupied) return;
                    updateFamily({ planId: plan.id as PlayerPlanId });
                  }}
                />
              );
            })}
          </Box>

          {family.category === 'family' && (
            <Box sx={{ ...quietCardSx, bgcolor: '#f8fafc' }}>
              <Typography sx={{ fontWeight: 800, color: brandColors.navy, fontSize: '0.98rem' }}>
                Extra children
              </Typography>
              <Typography variant="body2" sx={{ color: brandColors.slate, mt: 0.4, mb: 1 }}>
                Public profile is included with every plan. Extra children are the only household extra.
              </Typography>
              <StepperRow
                label="Additional children"
                hint={`${familyIncludedSeats(catalog)} included · extra child ${money(catalog.extraChildUsd)} / year`}
                value={family.extraChildren ?? 0}
                min={0}
                max={Math.max(0, catalog.maxChildren - catalog.familyHousehold.includedChildren)}
                onChange={(extraChildren) => {
                  if (!occupied) updateFamily({ extraChildren });
                }}
              />
            </Box>
          )}

          <Box sx={quietCardSx}>
            {selectedEnabled ? (
              <>
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <Typography
                    sx={{
                      fontFamily: '"Sora", Arial, sans-serif',
                      fontWeight: 900,
                      fontSize: '2rem',
                      color: brandColors.navy,
                      lineHeight: 1,
                    }}
                  >
                    {quote ? money(quote.payableUsd) : '—'}
                  </Typography>
                  <Typography sx={{ color: brandColors.slate, fontWeight: 600 }}>/ year</Typography>
                </Stack>
                {quote?.earlyBirdActive && (
                  <Typography
                    variant="body2"
                    sx={{ color: brandColors.slate, mt: 0.5, fontSize: '0.85rem', fontWeight: 600 }}
                  >
                    {earlyBirdUsuallyLine(quote.discountPercent, money(quote.listUsd))}
                  </Typography>
                )}
              </>
            ) : (
              <Typography sx={{ fontWeight: 800, color: brandColors.navy, lineHeight: 1.45 }}>
                {DISABLED_PLAN_LAUNCH_NOTE}
              </Typography>
            )}
            <Typography variant="body2" sx={{ color: brandColors.slate, mt: 0.75 }}>
              {familyPlanSummary(family)}
            </Typography>
            {selectedEnabled && quote && quote.extraChildren > 0 && (
              <Typography
                variant="caption"
                sx={{ color: brandColors.slate, display: 'block', mt: 0.35, fontWeight: 600 }}
              >
                + {quote.extraChildren} extra {quote.extraChildren === 1 ? 'child' : 'children'} included
              </Typography>
            )}

            {active && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Yearly plan is active{expiry ? ` until ${expiry}` : ''}.
              </Alert>
            )}
            {subscription?.status === 'prebooked' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Pre-booked {subscription.planConfig ? planDisplayName(subscription.planConfig) : ''}
                {formatExpiry(subscription.planConfig?.startDate || subscription.startedAt)
                  ? ` · starts ${formatExpiry(subscription.planConfig?.startDate || subscription.startedAt)}`
                  : ''}
                . This appears on Billing & invoices.
              </Alert>
            )}
            {subscription?.status === 'beta' && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Yearly plan is active{expiry ? ` until ${expiry}` : ''}.
              </Alert>
            )}
            {subscription?.status === 'cancelled' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Subscription cancelled{expiry ? ` · access until ${expiry}` : ''}.
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {!occupied && user.canEditProfile !== false && (
              <Box sx={{ mt: 2 }}>
                <PaymentGatewayChooser
                  value={payGateway}
                  suggested={suggestedGateway}
                  onChange={(next) => {
                    setGatewayTouched(true);
                    setPayGateway(next);
                  }}
                />
              </Box>
            )}

            {!occupied && user.canEditProfile !== false && (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  disabled={!canBuy}
                  onClick={handlePurchase}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    bgcolor: brandColors.orange,
                    '&:hover': { bgcolor: '#f07a3d' },
                  }}
                >
                  {!selectedEnabled
                    ? 'Purchase unavailable'
                    : quote
                      ? `Purchase ${money(quote.payableUsd)} / year`
                      : 'Purchase'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setPrebookOpen(true)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    borderColor: brandColors.navy,
                    color: brandColors.navy,
                  }}
                >
                  Pre-book
                </Button>
              </Stack>
            )}
            {!occupied && (
              <Typography variant="body2" sx={{ color: brandColors.slate, mt: 1.25 }}>
                You can choose sports after payment.
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, lineHeight: 1.5 }}>
              {catalog.termsNote}
            </Typography>
          </Box>
        </>
      )}

      {isOrgAdminRole(role) &&
        relevant.map((offer) => (
          <Box key={offer.id} sx={quietCardSx}>
            <Typography
              sx={{
                fontWeight: 800,
                color: brandColors.navy,
                fontFamily: '"Sora", Arial, sans-serif',
                fontSize: '1.15rem',
              }}
            >
              {offer.id === 'schools' ? 'Schools/Colleges' : offer.name}
            </Typography>
            <Typography variant="body2" sx={{ color: brandColors.slate, mt: 0.35, mb: 1.5 }}>
              {offer.audience}
            </Typography>
            {offer.offerHeadline && (
              <Typography sx={{ fontWeight: 700, color: brandColors.navy, mb: 1.25 }}>
                {offer.offerHeadline}
              </Typography>
            )}
            <Stack spacing={0.85} sx={{ mb: 2 }}>
              {offer.features.slice(0, 6).map((feature) => (
                <Stack key={feature} direction="row" spacing={1} alignItems="flex-start">
                  <CheckIcon sx={{ fontSize: 18, color: brandColors.blue, mt: '2px' }} />
                  <Typography sx={{ fontSize: '0.92rem', color: '#1e293b' }}>{feature}</Typography>
                </Stack>
              ))}
            </Stack>
            <Button
              variant="contained"
              onClick={() => navigate(purchasePathForOffer(offer))}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                bgcolor: brandColors.navy,
                '&:hover': { bgcolor: '#0a0f28' },
              }}
            >
              {offer.ctaLabel || 'Contact sales'}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              {offer.termsNote || '*Terms & conditions apply.'}
            </Typography>
          </Box>
        ))}

      <ChangePlanDialog
        open={changeOpen}
        onClose={() => setChangeOpen(false)}
        subscription={subscription}
        catalog={catalog}
        offer={playerOffer}
        onChanged={applySubscription}
      />
      <PrebookDialog
        open={prebookOpen}
        onClose={() => setPrebookOpen(false)}
        catalog={catalog}
        offer={playerOffer}
        initialConfig={family}
        defaultContact={user.email}
        onPrebooked={applySubscription}
      />
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontFamily: '"Sora", Arial, sans-serif', fontWeight: 800, color: brandColors.navy }}>
          Cancel subscription
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: brandColors.slate }}>
            Cancel this yearly plan? Status becomes Cancelled
            {expiry ? ` · access continues until ${expiry}` : ''}.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCancelOpen(false)} sx={{ textTransform: 'none', fontWeight: 700, color: brandColors.slate }}>
            Keep plan
          </Button>
          <Button
            variant="contained"
            disabled={cancelling}
            onClick={handleCancel}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: brandColors.navy,
              '&:hover': { bgcolor: '#0a0f28' },
            }}
          >
            {cancelling ? 'Cancelling…' : 'Cancel subscription'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default DashboardSubscriptionPanel;
